import React, { useState, useEffect, useCallback } from "react";
import * as api from "./adAccountApi";

const PROVIDER_LABEL = { google: "Google Ads", meta: "Meta Ads" };

// Row for one connected provider: shows the selected ad account, or a picker
// when the token can access several and none is chosen yet.
function ConnectionRow({ conn, canLink, onPick, onDisconnect }) {
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      await onPick(conn.id, choice);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="file-item" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="file-meta">
          <strong>{PROVIDER_LABEL[conn.provider] || conn.provider}</strong>
          <span className="proj-sub">
            {conn.accountId
              ? `${conn.accountName || conn.accountId} · ${conn.accountId}`
              : conn.needsSelection
              ? "Choose an ad account below"
              : "connected"}
          </span>
        </div>
        {canLink && (
          <button className="proj-link proj-link-danger" onClick={() => onDisconnect(conn.id)}>
            disconnect
          </button>
        )}
      </div>

      {/* Picker — shown when several ad accounts are available and none is chosen. */}
      {canLink && conn.needsSelection && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} style={{ minWidth: 220 }}>
            <option value="">Select an ad account…</option>
            {conn.availableAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name && a.name !== a.id ? `${a.name} (${a.id})` : a.id}
              </option>
            ))}
          </select>
          <button className="proj-btn" disabled={busy || !choice} onClick={pick}>
            {busy ? "Saving…" : "Use this account"}
          </button>
        </div>
      )}

      {/* Let the owner switch to a different account later. */}
      {canLink && conn.accountId && conn.availableAccounts.length > 1 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span className="proj-muted" style={{ fontSize: 12 }}>Change:</span>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Pick another…</option>
            {conn.availableAccounts
              .filter((a) => String(a.id) !== String(conn.accountId))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name && a.name !== a.id ? `${a.name} (${a.id})` : a.id}
                </option>
              ))}
          </select>
          <button className="proj-btn" disabled={busy || !choice} onClick={pick}>
            {busy ? "Saving…" : "Switch"}
          </button>
        </div>
      )}
    </div>
  );
}

// Links/unlinks the ad accounts for a project. The owner — or an admin/manager
// on their behalf — can connect Google/Meta via OAuth and pick which account.
export default function AdAccountPanel({ projectId, canLink, onChange }) {
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.listAdAccounts(projectId).then(setAccounts).catch((e) => setError(e.response?.data?.message || e.message));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const connect = async (provider) => {
    setBusy(true);
    setError(null);
    try {
      const url = await api.connectAdAccount(provider, window.location.pathname, projectId);
      window.location.href = url; // leave the SPA for the provider consent screen
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setBusy(false);
    }
  };

  const pick = async (id, accountId) => {
    setError(null);
    try {
      await api.selectAdAccount(id, accountId);
      load();
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const disconnect = async (id) => {
    await api.deleteAdAccount(id);
    load();
    onChange?.();
  };

  const hasProvider = (p) => accounts.some((a) => a.provider === p);

  return (
    <div className="proj-panel">
      <div className="proj-progress-head">
        <h4 style={{ margin: 0 }}>Ad accounts</h4>
      </div>
      {error && <div className="proj-alert">{error}</div>}

      <div className="file-list">
        {accounts.length === 0 && <p className="proj-muted">No ad account linked yet.</p>}
        {accounts.map((a) => (
          <ConnectionRow key={a.id} conn={a} canLink={canLink} onPick={pick} onDisconnect={disconnect} />
        ))}
      </div>

      {canLink ? (
        <div className="proj-strategy-actions">
          <button className="proj-btn" disabled={busy} onClick={() => connect("google")}>
            {hasProvider("google") ? "Reconnect Google Ads" : "+ Connect Google Ads"}
          </button>
          <button className="proj-btn" disabled={busy} onClick={() => connect("meta")}>
            {hasProvider("meta") ? "Reconnect Meta Ads" : "+ Connect Meta Ads"}
          </button>
        </div>
      ) : (
        accounts.length === 0 && (
          <p className="proj-hint">You don't have permission to link an ad account for this project.</p>
        )
      )}
    </div>
  );
}
